import { fileApi } from '@/api/file';
import { updateElementInHtml } from '@/lib/domUtils';
import type {
  ElementContentUpdates,
  ElementUpdateTargetIds,
  ElementValueUpdate,
} from '@/lib/domUtils';
import { isDomTargetRef } from '@/lib/domTargetRef';
// @ts-expect-error: linker-parent is a generated JS file without declaration
export { MessageType } from '@/lib/linker-parent.es.femf3n5t.js';
// @ts-expect-error: linker-parent is a generated JS file without declaration
import { MessageType } from '@/lib/linker-parent.es.femf3n5t.js';

/**
 * 获取默认的项目类型 ID
 */
export function getDefaultProjectTypeId(): number {
  return Number(import.meta.env.VITE_SSH_PROJECT_TYPE_ID);
}

/**
 * 判断是否为特殊项目类型（非默认的 999 模式）
 */
export function isSpecialProjectType(id?: number | string | null): boolean {
  // 注意：0 在这里被视为特殊模式（对应 null 逻辑）
  if (id === undefined) return false;
  return Number(id) !== getDefaultProjectTypeId();
}

interface LinkerHost {
  send: (type: string, data: Record<string, unknown>) => void;
}

/**
 * 同步元素更新到预览（通过 Linker）
 */
export const syncElementUpdatesToPreview = (
  linkerHost: LinkerHost | null | undefined,
  elementId: string,
  updates: {
    newText?: string;
    newTextItems?: ElementValueUpdate[];
    newSrc?: string;
    newHref?: string;
    newHrefItems?: ElementValueUpdate[];
  },
  targetIds?: ElementUpdateTargetIds,
) => {
  if (!linkerHost || Object.keys(updates).length === 0) {
    return;
  }

  const payloads = new Map<string, Record<string, unknown>>();
  const resolveLinkerTargetId = (targetRef: string | undefined, fallbackId?: string) => {
    if (!targetRef || isDomTargetRef(targetRef)) {
      if (!fallbackId || isDomTargetRef(fallbackId)) {
        return undefined;
      }
      return fallbackId;
    }
    return targetRef;
  };

  const appendPayload = (targetId: string, payload: Record<string, unknown>) => {
    payloads.set(targetId, {
      ...(payloads.get(targetId) || {}),
      ...payload,
    });
  };

  if (updates.newTextItems?.length) {
    updates.newTextItems.forEach((item) => {
      const targetId =
        resolveLinkerTargetId(item.targetId) ||
        (updates.newTextItems?.length === 1
          ? resolveLinkerTargetId(targetIds?.textTargetId, elementId)
          : undefined);
      if (!targetId) return;
      appendPayload(targetId, { newText: item.value });
    });
  } else if (updates.newText !== undefined) {
    appendPayload(resolveLinkerTargetId(targetIds?.textTargetId, elementId) || elementId, {
      newText: updates.newText,
    });
  }

  if (updates.newSrc !== undefined) {
    appendPayload(resolveLinkerTargetId(targetIds?.srcTargetId, elementId) || elementId, {
      newSrc: updates.newSrc,
    });
  }

  if (updates.newHrefItems?.length) {
    updates.newHrefItems.forEach((item) => {
      const targetId =
        resolveLinkerTargetId(item.targetId) ||
        (updates.newHrefItems?.length === 1
          ? resolveLinkerTargetId(targetIds?.hrefTargetId, elementId)
          : undefined);
      if (!targetId) return;
      appendPayload(targetId, { newHref: item.value });
    });
  } else if (updates.newHref !== undefined) {
    appendPayload(resolveLinkerTargetId(targetIds?.hrefTargetId, elementId) || elementId, {
      newHref: updates.newHref,
    });
  }

  payloads.forEach((payload, targetId) => {
    linkerHost.send(MessageType.MODIFY_TEXT, {
      id: targetId,
      ...payload,
    });
  });
};

/**
 * 执行本地项目文件更新并通过 Linker 通知预览同步
 */
export const updateProjectContent = async (
  projectId: string | number,
  elementId: string,
  updates: ElementContentUpdates,
  linkerHost?: LinkerHost | null | undefined,
  targetIds?: ElementUpdateTargetIds,
) => {
  console.log('[projectUtils] updateProjectContent called with:', {
    projectId,
    elementId,
    updates,
  });

  if (updates.text !== undefined) updates.text = updates.text.trim();
  if (updates.textItems?.length) {
    updates.textItems = updates.textItems.map((item) => ({
      ...item,
      value: item.value.trim(),
    }));
  }
  if (updates.src !== undefined) updates.src = updates.src.trim();
  if (updates.href !== undefined) updates.href = updates.href.trim();
  if (updates.hrefItems?.length) {
    updates.hrefItems = updates.hrefItems.map((item) => ({
      ...item,
      value: item.value.trim(),
    }));
  }

  if (Object.keys(updates).length === 0) {
    console.log('[projectUtils] No updates to apply, returning early');
    return;
  }

  try {
    console.log('[projectUtils] Fetching file list...');
    const files = await fileApi.listFiles({
      projectId: Number(projectId),
      path: 'dist',
    });
    console.log('[projectUtils] Files found:', files.length);

    const indexFile = files.find((f) => f.name === 'index.html' && !f.directory);
    if (!indexFile) {
      console.error('[projectUtils] index.html not found in files:', files);
      throw new Error('index.html not found in dist folder');
    }

    const targetPath = indexFile.path.startsWith('dist/')
      ? indexFile.path
      : `dist/${indexFile.path}`;
    console.log('[projectUtils] Target path:', targetPath);

    console.log('[projectUtils] Reading file content...');
    const fileContent = await fileApi.readFile({
      projectId: Number(projectId),
      path: targetPath,
    });
    console.log('[projectUtils] File content length:', fileContent.content.length);

    console.log('[projectUtils] Updating element in HTML...');
    const updatedContent = updateElementInHtml(fileContent.content, elementId, updates, targetIds);
    console.log('[projectUtils] Updated content length:', updatedContent.length);

    console.log('[projectUtils] Writing file...');
    await fileApi.writeFile({
      projectId: Number(projectId),
      path: targetPath,
      content: updatedContent,
    });
    console.log('[projectUtils] File written successfully');

    // 同时通过 Linker 通知预览同步修改内容
    console.log('[projectUtils] Syncing to preview via Linker...');
    syncElementUpdatesToPreview(
      linkerHost,
      elementId,
      {
        newText: updates.text,
        newTextItems: updates.textItems,
        newSrc: updates.src,
        newHref: updates.href,
        newHrefItems: updates.hrefItems,
      },
      targetIds,
    );

    console.log('[Studio] index.html content updated via API');
  } catch (error) {
    console.error('[Studio] Failed to update file via API:', error);
    throw error;
  }
};
